/**
 * Pure utility functions for ScheduleSetupWizard.
 * Extracted to enable unit testing without rendering the component.
 *
 * NOTE: `OUTFIT_EVENT_NAME` lives here (not in ActiveScheduleHome) so that
 * ScheduleSetupWizard can import it without creating a circular dependency.
 */

import { v4 as uuid } from "uuid";
import { ICustomScheduleEvent, ITaskItem } from "../ts/Definitions";
import {
  AvailabilityDayType,
  AvailabilityType,
  EventDay,
  EventType,
  ItemTypes,
} from "../ts/enums";
import { getDayHours } from "../utilities/dateUtils";

// ── Constants ─────────────────────────────────────────────────────────────────

export const OUTFIT_EVENT_NAME = "Chosen Outfit";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AiGeneratedOutfitOption {
  name: string;
  itemsUsed: string[];
  itemOptions: Record<string, string>;
}

export interface BuildSchedulePromptArgs {
  aiTheme: string;
  aiPersona: string;
  aiStartTime: string;
  aiEndTime: string;
  createExtraEvents: boolean;
  extraEventCount: number;
  itemListText?: string;
  gameTypesText?: string;
  penaltiesText?: string;
  proofRequestsText?: string;
  missEventPenaltiesText?: string;
}

export function buildSchedulePrompt({
  aiTheme,
  aiPersona,
  aiStartTime,
  aiEndTime,
  createExtraEvents,
  extraEventCount,
  itemListText = "",
  gameTypesText = "",
  penaltiesText = "",
  proofRequestsText = "",
  missEventPenaltiesText = "",
}: BuildSchedulePromptArgs): string {
  return `You are generating ONE self-contained daily schedule.

STATELESS MODE:
Ignore any previous messages, memories, examples, or earlier schedules. Use only this prompt and do not reuse material from past requests.

THEME:
"${aiTheme}"

PERSONA:
${aiPersona}

THEME LOCK — MANDATORY:
- Every single event must clearly and directly match the theme.
- do not drift into generic tasks, chores, punishments, or filler.
- If a task cannot be made to fit the theme, leave it out.
- Example: if the theme is "footballer for the day with skill tests", every event should involve footballer practice or measurable skill tests such as dribbling, passing, juggling, sprint, shooting, or match analysis.
- Every task MUST be written as a precise, numbered sequence of steps — never as a vague directive. Descriptions must be at least 30 words. The instruction must be so complete that the submissive has zero decisions to make; if they have to guess, the task has failed.
    * WRONG: "do chores" — RIGHT: "Scrub the kitchen floor on hands and knees using a cloth (not a mop) for exactly 20 minutes, then wipe all surfaces with a damp cloth, then clean the toilet with a brush and bleach."
    * WRONG: "buy lunch" — RIGHT: "Go to [nearest coffee shop], order exactly one medium black coffee and one plain croissant, pay, and return immediately without browsing."
    * WRONG: "maintain good posture" — RIGHT: "Sit with spine fully straight, both feet flat on the floor hip-width apart, hands folded in lap, chin parallel to the ground, shoulders rolled back — correct any deviation within 5 seconds."
    * WRONG: "expose yourself in public" — RIGHT: "Stand at the agreed spot for exactly 5 minutes with [item] removed, hands clasped behind the back, gaze forward, without covering or adjusting."
    * For physical tasks: state exact repetitions, sets, distances, durations, and any rest periods.
    * For creative or written tasks: state exact minimum word count, specific questions to answer, or format required.
    * For outfit or appearance tasks: name every garment by colour and fit, describe exactly how it is worn, and list what must remain visible or concealed.
    * For purchasing or errand tasks: specify the exact product, quantity, shop, payment method, and time limit.

SPECIFICITY + VERIFICATION — MANDATORY:
Every event must be specific, measurable, and objectively verifiable.
Use exact counts, distances, durations, targets, repetitions, or pass/fail conditions.
Each event must state exactly what to do, how success is measured, and what proof would show completion.

Use separate fields for event text:
- title: short heading for the event.
- description: full detailed instructions, success checks, proof requirements, and item list.
- name: set this to the same value as title for compatibility.

SCHEDULE RULES:
- Include "Day begins" at ${aiStartTime}.
- Include "Day ends" at ${aiEndTime}.
${createExtraEvents ? `- Create exactly ${extraEventCount} additional themed events between those times.
` : ""}- Never mention clock times inside the name text; put times only in minTime and maxTime.
- Keep the whole day coherent to the same theme from start to finish.
- Only use available items when they genuinely support the theme; do not force unrelated items into the schedule.${itemListText}${gameTypesText}${penaltiesText}${proofRequestsText}${missEventPenaltiesText}

Return ONLY valid JSON. No markdown. No explanation.

Each event object must include at least these fields:
{
  "title": "...",
  "description": "...",
  "name": "...",
  "minTime": "HH:mm",
  "maxTime": "HH:mm",
  "taskKeys": [],
  "gameKeys": [],
  "proofTaskKeys": [],
  "missEventPenalties": [],
  "missEventPillories": [],
  "gameResultPenalties": [],
  "itemOptions": {},
  "approvalChance": 1,
  "eventType": 0,
  "availability": 0,
  "disableAdd": false,
  "hasOptions": false,
  "optionAppliedChance": 1,
  "itemType": 1,
  "key": "event-short-name",
  "img": "",
  "imgId": "",
  "categories": [],
  "requiredTasksInProgress": [],
  "penalties": [],
  "pickSingleTask": false,
  "pickSinglePenalty": false,
  "pickSinglePillory": false,
  "pickSingleCategoryTask": false,
  "webLinkOptions": false,
  "optionImages": [],
  "specificDays": [],
  "runTasksToNextEvent": false,
  "availabilityDayType": 0
}

Return ONLY the JSON array.`;
}

// ── String helpers ────────────────────────────────────────────────────────────

/**
 * Strips markdown code-fence wrappers (```json ... ``` or ``` ... ```) that AI
 * assistants sometimes wrap their JSON output in.
 */
export function stripMarkdownFences(text: string): string {
  let t = text.trim();
  if (t.startsWith("```json")) t = t.replace(/^```json\s*/, "").replace(/```\s*$/, "");
  else if (t.startsWith("```")) t = t.replace(/^```\s*/, "").replace(/```\s*$/, "");
  return t.trim();
}

// ── Schedule event helpers ────────────────────────────────────────────────────

/**
 * Regex that matches common "time-of-day" prefixes that AIs prepend to event
 * names even though they were instructed not to.
 *
 * Examples matched:
 *  "At 08:00, Morning Exercise"
 *  "From 09:00 to 10:00, Yoga"
 *  "Starting at 14:30, Afternoon task"
 *
 * NOTE: Do NOT cache this as a module-level variable — the `g` flag makes a
 * RegExp instance stateful (lastIndex), so reusing the same object across
 * multiple .replace() calls would silently skip matches on odd-numbered calls.
 * Callers should construct a new instance or use this factory.
 */
export function makeTimeOfDayRegex(): RegExp {
  return /^(from\s+\d{1,2}:\d{2}(\s+to\s+\d{1,2}:\d{2})?[,\s]+|at\s+\d{1,2}:\d{2}[,\s]+|starting\s+at\s+\d{1,2}:\d{2}[,\s]+)/im;
}

/**
 * Sort events by minTime (ascending) and strip time-of-day prefixes from names.
 * Also ensures every event has `runTasksToNextEvent: true`.
 *
 * Returns a new array; the input is not mutated.
 */
export function normalizeScheduleEvents(
  events: ICustomScheduleEvent[]
): ICustomScheduleEvent[] {
  const usedKeys = new Set<string>();
  return [...events]
    .sort((a, b) => {
      const [ah, am] = a.minTime!.split(":").map(Number);
      const [bh, bm] = b.minTime!.split(":").map(Number);
      return ah !== bh ? ah - bh : am - bm;
    })
    .map((e, i) => {
      const legacyName = (e.name ?? "").replace(makeTimeOfDayRegex(), "").trimStart();
      const explicitTitle = (e.title ?? "").replace(makeTimeOfDayRegex(), "").trim();
      const explicitDescription = (e.description ?? "").trim();

      let normalizedTitle = explicitTitle;
      let normalizedDescription = explicitDescription;

      // Backward compatibility: legacy AI/event payloads packed title + details into `name` with newlines.
      if (!normalizedTitle && legacyName.includes("\n")) {
        const [first, ...rest] = legacyName.split("\n");
        normalizedTitle = (first ?? "").trim();
        if (!normalizedDescription) normalizedDescription = rest.join("\n").trim();
      }

      if (!normalizedTitle) normalizedTitle = legacyName || normalizedDescription;

      // Always generate fresh keys for AI-applied events so replacement acts as
      // a full replace and cannot inherit identity/rating collisions from a prior schedule.
      const baseKey = `event-${normalizedTitle}-${uuid()}`.trim();
      // Ensure uniqueness within this event set (mocked uuid or duplicate names).
      let key = baseKey;
      if (usedKeys.has(key)) key = `${baseKey}-${i}`;
      usedKeys.add(key);
      return {
        ...e,
        key,
        title: normalizedTitle,
        description: normalizedDescription,
        name: normalizedTitle,
        runTasksToNextEvent: true,
      };
    });
}

// ── Outfit helpers ────────────────────────────────────────────────────────────

/**
 * Determine which outfit task to use for wizard completion.
 *
 * - If `selectedOutfitTask` is defined, return it (user selected a known task).
 * - If the user typed a new theme name AND AI options were generated, synthesise
 *   a brand-new `ITaskItem` so the options are persisted to the database.
 * - Otherwise return `undefined` (no outfit step used).
 *
 * @param makeKey injectable key factory (defaults to uuid) — override in tests
 *                to produce deterministic keys.
 */
export function resolveOutfitTask(
  selectedOutfitTask: ITaskItem | undefined,
  outfitThemeName: string,
  parsedOutfitOptions: AiGeneratedOutfitOption[],
  makeKey: () => string = uuid
): ITaskItem | undefined {
  if (selectedOutfitTask) return selectedOutfitTask;
  if (!outfitThemeName.trim() || parsedOutfitOptions.length === 0) return undefined;
  return {
    key: makeKey(),
    name: outfitThemeName.trim(),
    description: "",
    img: "",
    imgId: "",
    itemsUsed: [],
    choiceNames: {},
    itemOptions: {},
    optionImages: [],
    options: [],
    categories: ["outfit"],
    itemType: ItemTypes.task,
    isItem: false,
    isCustom: true,
  } as unknown as ITaskItem;
}

/**
 * Determine which AI-generated (or saved) option the user picked.
 *
 * AI-generated options (selectedOutfitOptionIdx) take precedence over saved
 * ones (selectedExistingOptionIdx) so that freshly generated options win when
 * both selections happen to be active.
 */
export function selectPickedOption(
  parsedOutfitOptions: AiGeneratedOutfitOption[],
  selectedOutfitOptionIdx: number | null,
  existingOptions: AiGeneratedOutfitOption[],
  selectedExistingOptionIdx: number | null
): AiGeneratedOutfitOption | undefined {
  if (selectedOutfitOptionIdx !== null) return parsedOutfitOptions[selectedOutfitOptionIdx];
  if (selectedExistingOptionIdx !== null) return existingOptions[selectedExistingOptionIdx];
  return undefined;
}

/**
 * Merge newly AI-generated outfit options into a (possibly new) outfit task.
 * Returns a new object; the input task is not mutated.
 * Appends options after any that already exist on the task.
 */
export function buildUpdatedOutfitTask(
  outfitTask: ITaskItem,
  parsedOutfitOptions: AiGeneratedOutfitOption[]
): ITaskItem {
  const updated = { ...outfitTask };
  if (!updated.itemsUsed) updated.itemsUsed = [];
  if (!updated.choiceNames) updated.choiceNames = {};
  if (!updated.itemOptions) updated.itemOptions = {};
  parsedOutfitOptions.forEach((opt) => {
    const newIdx = updated.itemsUsed!.length;
    updated.itemsUsed!.push(opt.itemsUsed);
    (updated.choiceNames as Record<number, string>)[newIdx] = opt.name;
    (updated.itemOptions as Record<number, Record<string, string>>)[newIdx] =
      opt.itemOptions || {};
  });
  return updated;
}

/**
 * Build the outfit schedule event that will appear at the start of the day.
 * `itemOptions` uses `isChoiceName: true` so the scheduler resolves the value
 * as a choice-name index rather than a raw option string.
 */
export function buildOutfitEvent(
  outfitTask: ITaskItem,
  pickedOption: AiGeneratedOutfitOption,
  selectedDay: EventDay,
  overrideTimes: Record<EventDay, number[]>
): ICustomScheduleEvent {
  const hours = getDayHours(selectedDay, overrideTimes);
  const startHour = hours[0] ?? 4;
  const minTime = `${startHour.toString().padStart(2, "0")}:00`;
  return {
    title: OUTFIT_EVENT_NAME,
    name: OUTFIT_EVENT_NAME,
    description: "",
    minTime,
    taskKeys: [outfitTask.key],
    itemOptions: { [outfitTask.key]: pickedOption.name },
    isChoiceName: true,
    eventType: EventType.standard,
    availability: AvailabilityType.daily,
    disableAdd: false,
    hasOptions: false,
    optionAppliedChance: 1,
    itemType: ItemTypes.event,
    key: `event-${OUTFIT_EVENT_NAME}`,
    img: "",
    imgId: "",
    categories: [],
    requiredTasksInProgress: [],
    proofTaskKeys: [],
    pillories: [],
    penalties: [],
    pickSingleTask: false,
    pickSinglePenalty: false,
    pickSinglePillory: false,
    pickSingleCategoryTask: false,
    webLinkOptions: false,
    optionImages: [],
    specificDays: [],
    maxTime: "",
    approvalChance: 1,
    runTasksToNextEvent: false,
    availabilityDayType: AvailabilityDayType.AllDays,
    missEventPenalties: [],
  };
}

/**
 * Assemble the final event list for the schedule.
 *
 * - If an outfit was picked, the outfit event is prepended and any previous
 *   outfit event in the parsed list is removed (preventing duplicates).
 * - Returns a new array; inputs are not mutated.
 */
export function buildFinalEventList(
  outfitTask: ITaskItem | undefined,
  pickedOption: AiGeneratedOutfitOption | undefined,
  parsedEvents: ICustomScheduleEvent[],
  selectedDay: EventDay,
  overrideTimes: Record<EventDay, number[]>
): ICustomScheduleEvent[] {
  let allEvents: ICustomScheduleEvent[] = parsedEvents.length > 0 ? [...parsedEvents] : [];
  if (outfitTask && pickedOption) {
    const outfitEvent = buildOutfitEvent(outfitTask, pickedOption, selectedDay, overrideTimes);
    allEvents = [outfitEvent, ...allEvents.filter((e) => e.name !== OUTFIT_EVENT_NAME)];
  }
  return allEvents;
}

// ── Outfit JSON validation ────────────────────────────────────────────────────

/**
 * Parse and validate an AI-generated outfit JSON string.
 * Returns the parsed options array on success, throws on failure.
 * Normalises missing `itemOptions` fields to `{}`.
 */
export function parseOutfitJson(json: string): AiGeneratedOutfitOption[] {
  const parsed = JSON.parse(stripMarkdownFences(json)) as AiGeneratedOutfitOption[];
  if (!Array.isArray(parsed)) throw new Error("Expected a JSON array");
  for (const opt of parsed) {
    if (!opt.name || !Array.isArray(opt.itemsUsed)) {
      throw new Error('Each option must have "name" and "itemsUsed" array');
    }
    if (!opt.itemOptions) opt.itemOptions = {};
  }
  return parsed;
}

// ── Schedule JSON validation ──────────────────────────────────────────────────

/**
 * Parse, validate, sort and normalise an AI-generated schedule JSON string.
 * Returns the sorted, normalised event array on success, throws on failure.
 */
export function parseScheduleJson(json: string): ICustomScheduleEvent[] {
  const events = JSON.parse(stripMarkdownFences(json)) as ICustomScheduleEvent[];
  if (!Array.isArray(events)) throw new Error("Expected an array of events");
  for (const e of events) {
    if (!e.minTime) throw new Error("Each event must have minTime");
    if (!(e.title?.trim() || e.name?.trim() || e.description?.trim())) {
      throw new Error("Each event must include at least one of title, name, or description");
    }
  }
  return normalizeScheduleEvents(events);
}
